<template>
  <v-list-item
    :key="file.id"
    density="compact"
    class="AppBarDocumentSearchResultsItem__item"
    data-locator="document-search-result"
    :disabled="!canNavigate"
    @click="onClick"
  >
    <v-list-item-title class="text-grey-800 font-weight-medium">
      <div class="d-flex align-center">
        <ClosdFildersIcon :document="file" size="16" class="mr-2" />
        <span
          data-locator="document-search-name"
          class="text-truncate"
          style="max-width: 480px;"
        >
          {{ file.name }}
        </span>
      </div>
    </v-list-item-title>

    <div class="ml-6 mt-1">
      <v-list-item-subtitle class="text-caption text-grey-700">
        <AppBarDocumentSearchResultsItemPath
          :file="file"
          @click:result="$emit('click:result')"
        />
      </v-list-item-subtitle>

      <AppBarDocumentSearchSnippet
        v-if="isSearchFullTextEnabled && file.highlight.length"
        class="mt-2"
        :variant="variant"
        :highlights="Array.isArray(file.highlight) ? file.highlight : []"
      />
    </div>
  </v-list-item>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import { getFileExtension, getRoomRouteTypeFromType, openDocumentsFolder } from '@/common/utils/files'
import { ROOM_DOCUMENT_VIEWER_ROUTE_NAME } from '@/router/routerData'

import AppBarDocumentSearchResultsItemPath from './AppBarDocumentSearchResultsItemPath.vue'
import AppBarDocumentSearchSnippet from './AppBarDocumentSearchSnippet.vue'
import { USER_ROLE, track } from '../pendo/agent'

export type FileSearchResultPathPart = {
  id: number | string
  name: string
}

export type FileSearchResult = {
  id: number | string
  name: string
  type: string
  fullPath: Array<FileSearchResultPathPart>
  mnemo?: string
  roomTypeType: string
  roomName: string
  highlight: string[] | string
}

const props = defineProps<{
  file: FileSearchResult
  variant?: 'menu' | 'page'
}>()

const emit = defineEmits(['click:result'])

const router = useRouter()
const store = useStore()

const isSearchFullTextEnabled = computed(() => store.getters['user/isSearchFullTextEnabled'])

const isFileSupportedByViewer = computed(() =>
  ['pdf'].includes(getFileExtension(props.file)),
)

const canNavigate = computed(() => !!props.file.mnemo)

function onClick() {
  if (!canNavigate.value) return

  if (isFileSupportedByViewer.value) {
    const targetRoute = {
      name: ROOM_DOCUMENT_VIEWER_ROUTE_NAME,
      params: {
        roomType: getRoomRouteTypeFromType(props.file.roomTypeType),
        mnemo: props.file.mnemo,
        fileId: props.file.id,
      },
    }
    const route = router.resolve(targetRoute)
    if (route.matched.length) {
      track('document_previewed', {
        document_id: props.file.id,
        project_mnemo: props.file.mnemo,
        user_role: store.getters['room/isCurrentUserPm'] ? USER_ROLE.PROJECT_MANAGER : USER_ROLE.GUEST,
        document_type: getFileExtension(props.file),
        entrypoint: 'search',
      })
      window.open(route.href, '_blank')
    } else {
      console.warn('Unresolved route for ROOM_DOCUMENT_VIEWER_ROUTE_NAME', props.file)
    }
  } else {
    const folderToOpenId = props.file.fullPath.length
      ? props.file.fullPath[props.file.fullPath.length - 1].id
      : 0
    openDocumentsFolder(folderToOpenId, props.file.mnemo, props.file.roomTypeType, props.file.id)
  }

  emit('click:result')
}
</script>

<style>
.AppBarDocumentSearchResultsItem__item {
  .v-list-item-subtitle {
    opacity: 1 !important;
  }
}
</style>
